/**
 * Module-level signal for immediate thread-switch feedback.
 *
 * The thread list emits `emitThreadSwitchStart()` on click, and the Layout
 * component subscribes via `onThreadSwitchStart()` to show a settling overlay
 * with a fade-in animation. Once the overlay is fully visible, the provided
 * `onReady` callback fires to trigger the actual thread switch.
 */

type Listener = (onReady: () => void) => void;

let listener: Listener | null = null;
let userSwitchPending = false;

export function onThreadSwitchStart(cb: Listener): () => void {
  listener = cb;
  return () => {
    listener = null;
  };
}

/**
 * Reads and clears the "a user-initiated switch just committed" flag. Lets the
 * switch-behavior hook tell a real user switch apart from an internal cloud-ID
 * migration — only a real switch goes through `emitThreadSwitchStart`.
 */
export function consumeUserThreadSwitch(): boolean {
  const pending = userSwitchPending;
  userSwitchPending = false;
  return pending;
}

export function emitThreadSwitchStart(onReady: () => void): void {
  const commit = () => {
    userSwitchPending = true;
    onReady();
  };

  if (listener) {
    listener(commit);
  } else {
    commit();
  }
}
